<template>
  <component
    :is="componentIs"
    :href="url"
    :type="buttonType"
    :class="[cssClasses, arrowClass, colorSchemeClass, iconClass, selectedClass]"
    class="navigational-item">
    <slot></slot><span>{{ text }}</span>
  </component>
</template>

<script>
export default {
  props: {
    isButton: {
      type: Boolean,
      default: false
    },
    url: {
      type: String,
      default: undefined
    },
    text: {
      type: String,
      default: ''
    },
    useArrow: {
      type: Boolean,
      default: true
    },
    colorScheme: {
      type: String,
      default: 'white'
    },
    isSelected: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    componentIs() {
      return this.isButton ? 'button' : 'a'
    },
    buttonType() {
      return this.isButton && 'button'
    },
    arrowClass() {
      return (!this.isButton && this.$slots.default === undefined && this.useArrow && !this.isSelected) && 'navigational-item--arrow'
    },
    colorSchemeClass() {
      return `navigational-item--${this.colorScheme}`
    },
    iconClass() {
      return this.$slots.default !== undefined && 'navigational-item--icon-left'
    },
    selectedClass() {
      return this.isSelected && 'navigational-item--is-selected'
    }
  }
}
</script>

<style lang="scss">
@mixin caret($color: color(icon-caret-gray)) {
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='23' viewBox='0 0 16 23' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.66 11.894L.88 3 2.9.78l12.22 11.114L2.908 23 .89 20.78z' fill='#{str-replace(inspect($color), '#', '%23')}' fill-rule='nonzero'/%3E%3C/svg%3E");
}

.navigational-item {
  @include body-semi-bold;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 3.125rem;
  padding-top: measure(eighth);
  padding-right: measure(quarter);
  padding-bottom: measure(eighth);
  padding-left: measure(quarter);
  color: color(text);
  text-decoration: none;
  text-align: left;
  border: 1px solid;

  &:hover {
    cursor: pointer;
  }

  @include media(medium) {
    min-height: 3.5rem;
    padding-top: measure(eighth);
    padding-right: measure(half);
    padding-bottom: measure(eighth);
    padding-left: measure(half);
  }
}

.category-page-categories .navigational-item {
  @include body;
  align-items: flex-start;
  // height: 2.5rem;
  min-height: initial;
  border-top: none;
  padding-top: measure(quarter);
  padding-bottom: measure(quarter);
}

.navigational-item--arrow {
  position: relative;
  padding-top: measure(eighth);
  padding-right: measure(full);
  padding-bottom: measure(eighth);
  padding-left: measure(quarter);

  &:after {
    @include caret;
    content: '';
    position: absolute;
    top: 50%;
    right: measure(quarter);
    width: 0.75rem;
    height: 1.3125rem;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    transform: translateY(-50%);
    pointer-events: none;
  }

  &:hover:after {
    @include caret(color(icon-caret-active));
  }

  @include media(medium) {
    padding-top: measure(eighth);
    padding-right: measure(full);
    padding-bottom: measure(eighth);
    padding-left: measure(half);
  }
}

.category-page-categories .navigational-item--arrow:after {
  height: 0.7rem;
}

.router-link-exact-active.navigational-item--arrow.navigational-item--tab {
  background-color: color(background-blue-2);
  border-color: color(background-blue-2);
  color: color(text-inverted);
  justify-content: center;

  &:after {
    display: none;
  }
}

.navigational-item--white {
  background-color: color(background-white);
  border-color: color(border-gray);
}

.navigational-item--light-blue {
  background-color: color(background-blue-5);
  border-color: color(border-blue-3);

  &:hover {
    background-color: color(background-blue-4);
  }
}

.navigational-item--dark-blue {
  background-color: color(background-blue-1);
  border-color: color(background-blue-1);
  color: color(text-inverted);

  &:after {
    @include caret(color(icon-caret-white));
  }
}

.navigational-item--gray {
  background-color: #3B3B3B;
  border-color: #3B3B3B;
  color: color(text-inverted);
  position: relative;
}

.navigational-item--gray .navigation-item__icon {
  margin-right: measure(quarter);
  width: 1.07625rem;
  height: 1.07625rem;
}

.navigational-item--gray .navigation-item__icon-right {
  position: absolute;
  top: 50%;
  right: measure(quarter);
  transform: translateY(-50%);
}

.navigation-market-selector--is-open .navigation-item__icon-right {
  transform: translateY(-50%) scale(-1);
}

.navigational-item--icon-left > *:first-child {
  max-height: 2.875rem;
  margin-right: measure(quarter);
}

.navigational-item--is-selected {
  position: relative;
  background-color: color(background-blue-4);
  pointer-events: none;

  &:hover {
    cursor: default;
  }

  &:after {
    content: '';
    position: absolute;
    top: 50%;
    right: measure(quarter);
    width: 1.875rem;
    height: 1.875rem;
    background-image: url("data:image/svg+xml,%3Csvg width='35' height='35' viewBox='0 0 35 35' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Ccircle fill='%23006DB6' cx='17.5' cy='17.5' r='17.5'/%3E%3Cpath d='M14.724 26.873L7 19.489l2.31-2.397 5.089 4.866L25.395 8.26 28 10.335z' fill='%23FFF'/%3E%3C/g%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    transform: translateY(-50%);
    pointer-events: none;
  }
}

.navigational-item--is-loading {
  position: relative;
  pointer-events: none;

  &:hover {
    cursor: default;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: measure(quarter);
    width: 1.375rem;
    height: 1.375rem;
    background-color: transparent;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    border: .2rem solid color(background-white);
    border-top-color: color(background-blue);
    animation: 800ms spin linear infinite;
    z-index: 2;
  }
}

.navigational-item__smaller-text {
  @include body-smaller;
}

.navigational-item--model-item {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-bottom: measure(eighth);
}

@keyframes spin-smaller {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
</style>
